import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider, defaultTheme } from '@adobe/react-spectrum';
import {
  Provider as S2Provider,
  ToastContainer as S2ToastContainer,
} from '@react-spectrum/s2';
import { BrowserRouter, useHref, useNavigate } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
// Initialize i18next before anything renders so `t()` resolves the real English
// catalog on first paint (no async load — resources are statically bundled).
import i18n from './i18n';
// S2 page-level styles (page background + color-scheme). Uses :where(:root) at
// zero specificity so the app's own index.css/token layer still wins; imported
// first so later app CSS cascades over it.
import '@react-spectrum/s2/page.css';
import './index.css';
import App from './App';
import { ThemeModeProvider, useThemeMode } from './contexts/ThemeModeContext';

// Suppress ResizeObserver loop error from React Spectrum TableView with column resizing
// This wraps ResizeObserver callbacks in requestAnimationFrame to prevent the loop error
const OriginalResizeObserver = window.ResizeObserver;
window.ResizeObserver = class ResizeObserver extends OriginalResizeObserver {
  constructor(callback: ResizeObserverCallback) {
    super((entries, observer) => {
      window.requestAnimationFrame(() => {
        callback(entries, observer);
      });
    });
  }
};

// defaultTheme ships both color schemes; the active one is driven by the
// ThemeModeProvider's mode (OS preference on first load, explicit user choice
// thereafter). The custom-CSS token layer flips in parallel via data-theme.
//
// Spectrum v3 and S2 coexist during the incremental migration: the v3 Provider
// wraps the S2 Provider, which is the inner-most provider as Adobe requires when
// mixing Spectrum versions. S2 links integrate with react-router via the shared
// router adapter so client-side navigation works from migrated components.
//
// Both providers' `locale` is driven from the active i18next language so
// Spectrum's own built-in strings, date, and number formatting localize
// alongside the app's externalized copy (today `en`; a future `de` flips both
// in lock-step).
const useActiveLocale = (): string => {
  const [locale, setLocale] = React.useState<string>(i18n.language || 'en');
  React.useEffect(() => {
    const handleLanguageChanged = (lng: string): void => setLocale(lng);
    i18n.on('languageChanged', handleLanguageChanged);
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, []);
  return locale;
};

const ThemedProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const locale = useActiveLocale();
  return (
    <Provider theme={defaultTheme} colorScheme={mode} locale={locale}>
      <S2Provider
        colorScheme={mode}
        background="base"
        locale={locale}
        router={{ navigate, useHref }}
      >
        <S2ToastContainer placement="top" />
        {children}
      </S2Provider>
    </Provider>
  );
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <I18nextProvider i18n={i18n}>
      <BrowserRouter>
        <ThemeModeProvider>
          <ThemedProvider>
            <App />
          </ThemedProvider>
        </ThemeModeProvider>
      </BrowserRouter>
    </I18nextProvider>
  </React.StrictMode>
);
